import React, { useState, useEffect, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import FileDropzone from './components/FileDropzone';
import ExpenseTable from './components/ExpenseTable';
import CategoryRules from './components/CategoryRules';
import ExpenseSummary from './components/ExpenseSummary';
import DataVisualizations from './components/DataVisualizations';
import BudgetManager from './components/BudgetManager';
import DateRangeSelector from './components/DateRangeSelector';
import Header from './components/Header';
import { CitiTransaction, ExpenseEntry, CategoryRule, BudgetItem, RuleMatchType, BUSINESS_CATEGORIES, EXPENSE_COGS_TYPES } from './types';
import { 
  parseFile,
  transformTransactions,
  generateExcelFile,
  merchantMappings
} from './utils/csvUtils';
import { isWithinInterval, parseISO } from 'date-fns';
import TagManager from './components/TagManager';
import { 
  initDB, 
  getAllItems, 
  addItem, 
  updateItem, 
  clearStore, 
  STORES 
} from './utils/dbUtils';

// Generate default category rules from merchant mappings
const generateDefaultCategoryRules = (): CategoryRule[] => {
  return Object.entries(merchantMappings).map(([pattern, mapping]) => ({
    pattern,
    category: mapping.category,
    expenseCOGS: mapping.expenseCOGS,
    matchType: RuleMatchType.CONTAINS,
    caseSensitive: false
  }));
};

const App: React.FC = () => {
  const [originalTransactions, setOriginalTransactions] = useState<CitiTransaction[]>([]);
  const [expenses, setExpenses] = useState<ExpenseEntry[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<ExpenseEntry[]>([]);
  const [categoryRules, setCategoryRules] = useState<CategoryRule[]>(generateDefaultCategoryRules());
  const [budgets, setBudgets] = useState<BudgetItem[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{start: Date, end: Date} | null>(null);
  const [activeTab, setActiveTab] = useState<'data' | 'summary' | 'budget' | 'rules'>('data');
  const [isLoading, setIsLoading] = useState(true);
  const [dbInitialized, setDbInitialized] = useState(false);

  // Initialize IndexedDB
  useEffect(() => {
    const initialize = async () => {
      try {
        await initDB();
        setDbInitialized(true);
      } catch (error) {
        console.error('Failed to initialize IndexedDB', error);
        alert('Warning: Failed to initialize database. Your data may not be saved between sessions.');
      }
    };
    
    initialize();
  }, []);

  // Load saved data from IndexedDB on initial load
  useEffect(() => {
    if (!dbInitialized) return;
    
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load rules
        let rules = await getAllItems<CategoryRule>(STORES.RULES);
        if (rules.length === 0) {
          // If no rules in IndexedDB, try localStorage as fallback
          const savedRules = localStorage.getItem('expense-processor-rules');
          if (savedRules) {
            try {
              rules = JSON.parse(savedRules);
              
              // Migrate rules to IndexedDB
              for (const rule of rules) {
                await addItem(STORES.RULES, rule);
              }
            } catch (error) {
              console.error('Error loading saved rules from localStorage:', error);
              rules = generateDefaultCategoryRules();
            }
          } else {
            rules = generateDefaultCategoryRules();
          }
        }
        setCategoryRules(rules);
        
        // Load budgets
        let savedBudgets = await getAllItems<BudgetItem>(STORES.BUDGETS);
        if (savedBudgets.length === 0) {
          // If no budgets in IndexedDB, try localStorage as fallback
          const localBudgets = localStorage.getItem('expense-processor-budgets');
          if (localBudgets) {
            try {
              savedBudgets = JSON.parse(localBudgets);
              
              // Migrate budgets to IndexedDB
              for (const budget of savedBudgets) {
                await addItem(STORES.BUDGETS, budget);
              }
            } catch (error) {
              console.error('Error loading saved budgets from localStorage:', error);
            }
          }
        }
        setBudgets(savedBudgets);
        
        // Load expenses
        let savedExpenses = await getAllItems<ExpenseEntry>(STORES.EXPENSES);
        if (savedExpenses.length === 0) {
          // If no expenses in IndexedDB, try localStorage as fallback
          const localExpenses = localStorage.getItem('expense-processor-expenses');
          if (localExpenses) {
            try {
              savedExpenses = JSON.parse(localExpenses);
              
              // Migrate expenses to IndexedDB
              for (const expense of savedExpenses) {
                await addItem(STORES.EXPENSES, expense);
              }
            } catch (error) {
              console.error('Error loading saved expenses from localStorage:', error);
            }
          }
        }
        setExpenses(savedExpenses);
        setFilteredExpenses(savedExpenses);
      } catch (error) {
        console.error('Error loading data from IndexedDB', error);
        alert('Failed to load saved data. Using default values.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [dbInitialized]);

  // Apply date filtering when expenses or date range changes
  useEffect(() => {
    if (!selectedDateRange) {
      setFilteredExpenses(expenses);
      return;
    }
    
    const { start, end } = selectedDateRange;
    
    // Filter expenses by date range
    const filtered = expenses.filter((expense) => {
      const dateParts = expense.date.split('/');
      if (dateParts.length !== 3) return false;
      
      const expenseDate = new Date(
        parseInt(dateParts[2]), // Year
        parseInt(dateParts[0]) - 1, // Month (0-indexed)
        parseInt(dateParts[1]) // Day
      );
      
      return expenseDate >= start && expenseDate <= end;
    });
    
    setFilteredExpenses(filtered);
  }, [expenses, selectedDateRange]);

  // Save expenses whenever they change
  useEffect(() => {
    if (isLoading || !dbInitialized || expenses.length === 0) return;
    
    const saveExpenses = async () => {
      try {
        // Clear existing expenses
        await clearStore(STORES.EXPENSES);
        
        // Add all expenses
        for (const expense of expenses) {
          await addItem(STORES.EXPENSES, expense);
        }
      } catch (error) {
        console.error('Error saving expenses to IndexedDB', error);
        
        // Fallback to localStorage
        try {
          localStorage.setItem('expense-processor-expenses', JSON.stringify(expenses));
        } catch (e) {
          console.error('Failed to save to localStorage as well', e);
        }
      }
    };
    
    saveExpenses();
  }, [expenses, isLoading, dbInitialized]);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    setIsProcessing(true);
    try {
      setFileName(file.name);
      const result = await parseFile(file);
      setOriginalTransactions(result);
      
      // Transform transactions to expense entries
      const newExpenses = transformTransactions(result, categoryRules);
      
      // Check if this appears to be a Citi statement
      const isCitiStatement = file.name.toLowerCase().includes('citi') || 
                             (result.length > 0 && 'Status' in result[0] && 'Debit' in result[0]);
      
      // Merge new expenses with existing ones, avoiding duplicates
      setExpenses(prevExpenses => {
        // Create a map of existing expenses for faster lookup
        const existingMap = new Map(
          prevExpenses.map(expense => [
            `${expense.date}-${expense.description}-${expense.amount}`,
            true
          ])
        );
        
        // Filter out duplicates
        const uniqueNewExpenses = newExpenses.filter(
          newExpense => 
            !existingMap.has(`${newExpense.date}-${newExpense.description}-${newExpense.amount}`)
        );
        
        // Show success message with count
        if (uniqueNewExpenses.length > 0) {
          alert(`Successfully imported ${uniqueNewExpenses.length} new transactions${isCitiStatement ? ' from Citi statement' : ''}.`);
        } else {
          alert('No new transactions to import. All entries already exist.');
        }
        
        return [...prevExpenses, ...uniqueNewExpenses];
      });
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please check the format and try again.');
    } finally {
      // Always ensure processing state is reset when done - this fixes the bug where 
      // the UI would still show "processing" even after completion
      setIsProcessing(false);
    }
  };

  const handleUpdateExpense = (index: number, updatedExpense: ExpenseEntry) => {
    const updatedExpenses = [...expenses];
    updatedExpenses[index] = updatedExpense;
    setExpenses(updatedExpenses);
  };

  const handleUpdateRules = async (rules: CategoryRule[]) => {
    setCategoryRules(rules);
    
    if (dbInitialized) {
      try {
        // Clear existing rules
        await clearStore(STORES.RULES);
        
        // Add all rules
        for (const rule of rules) {
          await addItem(STORES.RULES, rule);
        }
      } catch (error) {
        console.error('Error saving rules to IndexedDB', error);
        
        // Fallback to localStorage
        localStorage.setItem('expense-processor-rules', JSON.stringify(rules));
      }
    } else {
      // Fallback to localStorage if DB not initialized
      localStorage.setItem('expense-processor-rules', JSON.stringify(rules));
    }
    
    // Reapply rules to transactions
    if (originalTransactions.length > 0) {
      const updatedExpenses = transformTransactions(originalTransactions, rules);
      setExpenses(updatedExpenses);
    }
  };

  const handleAddCategoryRule = (rule: CategoryRule) => {
    const updatedRules = [...categoryRules, rule];
    handleUpdateRules(updatedRules);
  };

  const handleSaveBudgets = async (updatedBudgets: BudgetItem[]) => {
    setBudgets(updatedBudgets);
    
    if (dbInitialized) {
      try {
        // Clear existing budgets
        await clearStore(STORES.BUDGETS);
        
        // Add all budgets
        for (const budget of updatedBudgets) {
          await addItem(STORES.BUDGETS, budget);
        }
      } catch (error) {
        console.error('Error saving budgets to IndexedDB', error);
        
        // Fallback to localStorage
        localStorage.setItem('expense-processor-budgets', JSON.stringify(updatedBudgets));
      }
    } else {
      // Fallback to localStorage if DB not initialized
      localStorage.setItem('expense-processor-budgets', JSON.stringify(updatedBudgets));
    }
  };

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setSelectedDateRange({ start: startDate, end: endDate });
  };

  const getExportFileName = () => {
    if (fileName) {
      return `processed_${fileName.replace('.csv', '')}.csv`;
    }
    return 'processed_expenses.csv';
  };

  const getExcelFileName = () => {
    if (fileName) {
      return `processed_${fileName.replace('.csv', '')}.xlsx`;
    }
    return 'processed_expenses.xlsx';
  };

  const handleExcelExport = () => {
    const dataToExport = filteredExpenses.length > 0 ? filteredExpenses : expenses;
    const blob = generateExcelFile(dataToExport, BUSINESS_CATEGORIES);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', getExcelFileName());
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleClearData = async () => {
    if (window.confirm('Are you sure you want to clear all expense data? This cannot be undone.')) {
      setExpenses([]);
      setFilteredExpenses([]);
      setOriginalTransactions([]);
      
      if (dbInitialized) {
        try {
          await clearStore(STORES.EXPENSES);
        } catch (error) {
          console.error('Error clearing expenses from IndexedDB', error);
        }
      }
      
      // Also clear localStorage as a precaution
      localStorage.removeItem('expense-processor-expenses');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-medium text-gray-900 mb-4">Loading your data...</h2>
          <div className="animate-pulse flex space-x-4">
            <div className="rounded-full bg-gray-300 h-12 w-12"></div>
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header 
        activeTab={activeTab} 
        onChangeTab={setActiveTab} 
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'data' && (
          <div>
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900">Upload Statement</h2>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Upload a Citi credit card statement CSV or Excel file with expenses to process.</p>
                </div>
                <div className="mt-5">
                  <FileDropzone 
                    onFileUpload={handleFileUpload} 
                    isLoading={isProcessing}
                  />
                </div>
              </div>
            </div>

            {expenses.length > 0 && (
              <div className="bg-white shadow rounded-lg mb-6">
                <div className="px-4 py-5 sm:p-6">
                  <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Processed Expenses
                      </h3>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        {filteredExpenses.length} of {expenses.length} expenses shown
                      </p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <DateRangeSelector onDateRangeChange={handleDateRangeChange} />

                      <div className="inline-flex shadow-sm rounded-md">
                        <CSVLink 
                          data={filteredExpenses} 
                          filename={getExportFileName()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-l-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Download CSV
                        </CSVLink>
                        <button
                          type="button"
                          onClick={handleExcelExport}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          Download Excel
                        </button>
                      </div>
                      
                      <button
                        type="button"
                        onClick={handleClearData}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Clear Data
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <ExpenseTable 
                      expenses={filteredExpenses} 
                      onUpdateExpense={handleUpdateExpense}
                      onAddCategoryRule={handleAddCategoryRule}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'summary' && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Expense Summary</h2>
              {filteredExpenses.length > 0 ? (
                <>
                  <ExpenseSummary expenses={filteredExpenses} />
                  <div className="mt-8">
                    <DataVisualizations expenses={filteredExpenses} />
                  </div>
                </>
              ) : (
                <p className="text-gray-500">Upload a statement to view expense summaries.</p>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'budget' && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Budget Manager</h2>
              <BudgetManager
                savedBudgets={budgets}
                expenses={filteredExpenses}
                onSaveBudgets={handleSaveBudgets}
              />
            </div>
          </div>
        )}
        
        {activeTab === 'rules' && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Category Rules</h2>
              <CategoryRules
                rules={categoryRules}
                onUpdateRules={handleUpdateRules}
              />
            </div>
          </div>
        )}
      </main>
      
      <footer className="bg-white mt-8 py-4 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            Credit Card Statement Processor with Expense/COGS Categorization
          </p>
          <p className="text-center text-xs text-gray-400 mt-1">
            Data stored in your browser using IndexedDB
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App; 